import { ChainSymbol } from '../datas/chains';
import { TokenSymbol } from '../datas/tokens';
import http from '../utils/http';

const KSTADIUM_BRIDGE_BACKEND_URL =
  process.env.REACT_APP_KSTADIUM_BRIDGE_BACKEND_URL;
const KSTADIUM_BACKEND_URL = process.env.REACT_APP_KSTADIUM_BACKEND_URL!;

// 중요! 서버쪽에서 받아오는 ChainState와 타입 항상 일치시킬것
export type FromChainHtlcState =
  | 'pending'
  | 'newContract'
  | 'mirrored'
  | 'withdrawn'
  | 'refunded'
  | 'failed'
  | 'expired';
export type ToChainHtlcState = FromChainHtlcState;

// 변경시 적용
export interface HistoryPayload {
  id: number;
  sender: string;
  fromEvmChainId: number;
  toEvmChainId: number;
  fromChainSymbol: ChainSymbol;
  fromChainHtlcState: null | FromChainHtlcState;
  receiver: string;
  toChainSymbol: ChainSymbol;
  toChainHtlcState: null | ToChainHtlcState;
  amount: string;
  createdAt: string;
  fromChainTimelock: string;
  toChainTimelock?: string | null;
  hashlock: string;
  tokenSymbol: string;
  fromTokenAddress: string | null;
  toTokenAddress: string | null;
  fromTxHash: null | string;
  toTxHash: null | string;
}

export interface HtlcDataResponse {
  id: number;
  amount: string;
  fromContractId: string;
  hashlock: string;
  sender: string;
  timelock: number;
  toContractId: string;
  tokenAddress: string;
}

export interface PostHistoryResponse {
  id: number;
}

interface PostHistoryParams {
  fromChainSymbol: string;
  toChainSymbol: string;
  tokenSymbol: TokenSymbol;
  sender: string;
  receiver: string;
  fromChainTimelock: string;
  hashlock: string;
  amount: string;
}

interface PutHistoryParams {
  chain: 'from' | 'to';
  hash: string;
}

export interface ChainTransferTaxAll {
  data: ChainTransferTax[];
}
export interface ChainTransferTax {
  id: number;
  tax: string;
  minTaxAmount: string;
  tokenSymbol: TokenSymbol;
  chainSymbol: ChainSymbol;
}

export interface ChainMinTransferFee {
  id: number;
  evmChainId: number;
  minAmount: number;
  symbol: string;
  currencySymbol: string;
}

export interface ChainTransferBridgeFeeAll {
  data: ChainTransferBridgeFee[];
}
export interface ChainTransferBridgeFee {
  id: number;
  fee: string;
  symbol: ChainSymbol;
}

export interface ConvertAmountRangeResponse {
  id: number;
  createdAt: string;
  symbol: string;
  evmChainId: string;
  bridgeFee: string;
  promotionFee: string;
  tax: string;
  minTaxAmount: string;
  minAmount: string;
  maxAmount: string;
  erc20Address: string;
}
export interface ConvertAmountRangeResponseData {
  data: ConvertAmountRangeResponse[];
}

const BridgeBackend = {
  // id에 해당하는 히스토리 조회
  async getHistory(id: number): Promise<HistoryPayload> {
    return http.get(`${KSTADIUM_BRIDGE_BACKEND_URL}/api/histories/${id}`);
  },

  // 새 history 추가
  async postHistory(params: PostHistoryParams): Promise<PostHistoryResponse> {
    return http.post(`${KSTADIUM_BRIDGE_BACKEND_URL}/api/histories`, params);
  },

  // id에 해당하는 히스토리의 txHash 업데이트
  async putHistory(
    id: number,
    params: PutHistoryParams,
  ): Promise<{ id: number }> {
    return http.put(
      `${KSTADIUM_BRIDGE_BACKEND_URL}/api/histories/${id}`,
      params,
    );
  },

  // 거부시 putHistory로 업데이트 api가 아닌 reject api를 호출
  async reject(id: number): Promise<{ id: number }> {
    return http.put(
      `${KSTADIUM_BRIDGE_BACKEND_URL}/api/histories/reject/${id}`,
    );
  },

  // 전체 tax 정보
  async getTax(): Promise<ChainTransferTaxAll> {
    return http.get(
      `${KSTADIUM_BRIDGE_BACKEND_URL}/api/chains/transfers/taxes`,
    );
  },

  // 특정 통화 tax (정보 사용하는 곳 없음)
  async getSpecificTax(
    chainSymbol: string,
    tokenSymbol: string,
  ): Promise<ChainTransferTax> {
    return http.get(
      `${KSTADIUM_BRIDGE_BACKEND_URL}/api/chains/transfers/taxes/${chainSymbol}/${tokenSymbol}`,
    );
  },

  // 전체 bridgeFee
  async getBridgeFee(): Promise<ChainTransferBridgeFeeAll> {
    return http.get(`${KSTADIUM_BRIDGE_BACKEND_URL}/api/chains/transfers/fees`);
  },

  // 특정 bridgeFee
  async getSpecificBridgeFee(
    chainSymbol: string,
    tokenSymbol: string,
  ): Promise<ChainTransferBridgeFee> {
    return http.get(
      `${KSTADIUM_BRIDGE_BACKEND_URL}/api/chains/transfers/fees/${chainSymbol}/${tokenSymbol}`,
    );
  },

  // 전체 최소 transfer balance (사용하지 않고 있음)
  async getTransferFee(): Promise<ChainMinTransferFee> {
    return http.get(`${KSTADIUM_BRIDGE_BACKEND_URL}/api/chains/transfers/mins`);
  },

  // 특정 최소 transfer balance (사용하지 않고 있음)
  async getSpecificTransferFee(
    chainId: string,
    currencySymbol: string,
  ): Promise<ChainMinTransferFee> {
    return http.get(
      `${KSTADIUM_BRIDGE_BACKEND_URL}/api/chains/transfers/mins/${chainId}/${currencySymbol}`,
    );
  },

  // maxAmount, minAmount 전체 조회
  async getConvertAmountRangeAll(): Promise<ConvertAmountRangeResponse[]> {
    return http.get(`${KSTADIUM_BRIDGE_BACKEND_URL}/api/cryptocurrencies`);
  },

  // maxAmount, minAmount 조회
  async getConvertAmountRange(id: number): Promise<ConvertAmountRangeResponse> {
    return http.get(
      `${KSTADIUM_BRIDGE_BACKEND_URL}/api/cryptocurrencies/${id}`,
    );
  },

  async getCryptoCurrencyWithSymbol(
    chainSymbol: ChainSymbol,
    tokenSymbol: TokenSymbol,
  ): Promise<ConvertAmountRangeResponse> {
    return http.get(
      `${KSTADIUM_BRIDGE_BACKEND_URL}/api/cryptocurrencies/symbols/${chainSymbol}/${tokenSymbol}`,
      null,
      {
        headers: {
          Accept: `application / json`,
        },
      },
    );
  },
};

export default BridgeBackend;
